import { db } from '../db';
import { doRequest } from '../http';
import type {
  PreAnalysis,
  PreAnalysisConfig,
  PreAnalysisMode,
} from '@/model/pre-analysis.interface';
import { compressImage } from '../files/utils';
import { useStepVistoria } from '@/stores/stepVistoria';
import {
  convertArrayBufferToBlob,
  convertBase64ToBlob,
} from '@/shared/utils/convert';
import {
  PRE_ANALYSIS_MODE_OFF,
  PRE_ANALYSIS_MODE_ON,
  PRE_ANALYSIS_RESULT_ERROR,
  PRE_ANALYSIS_RESULT_REJECTED,
} from '@/global';

const headers = {
  Authorization: `Bearer ${import.meta.env.VITE_APP_API_VIDA_TOKEN}`,
};

export async function getPreAnalysisConfigAPI() {
  const { data, error } = await doRequest({
    url: `${
      import.meta.env.VITE_APP_API_VIDA_URL
    }/inspection-pre-analysis-config`,
    method: 'GET',
    headers,
  });

  const config: PreAnalysisConfig =
    !error && data
      ? {
          mode: data.mode,
          retriesToAllowSend: Number(data.retriesToAllowSend ?? 0),
        }
      : { mode: PRE_ANALYSIS_MODE_OFF, retriesToAllowSend: 0 };

  useStepVistoria().setPreAnalysisConfig(config);
}

export function withoutPreAnalysis(mode: PreAnalysisMode): PreAnalysis {
  return { mode, result: null, rejectionReason: null, analyzedAt: null };
}

async function getPhotoForAnalysis(fileId: string): Promise<Blob | null> {
  const file = await db.files.where('id').equals(fileId).first();

  if (!file || !file.type.startsWith('image/')) {
    return null;
  }

  const photo = convertArrayBufferToBlob(file.content, file.type);

  if (file.type === 'image/webp') {
    return photo;
  }

  const { dataUrl } = await compressImage(photo);

  return convertBase64ToBlob(dataUrl);
}

async function analyzePhotoAPI(
  key: string,
  photoModelId: number,
  photo: Blob,
  mode: PreAnalysisMode,
): Promise<PreAnalysis> {
  const form = new FormData();
  form.append('file', photo, `photo.${photo.type.split('/')[1] || 'jpg'}`);

  const { data, error } = await doRequest({
    url: `${
      import.meta.env.VITE_APP_API_VIDA_URL
    }/inspection/${key}/photos/${photoModelId}/pre-analysis`,
    method: 'POST',
    headers,
    data: form,
  });

  if (error || !data) {
    return {
      mode,
      result: PRE_ANALYSIS_RESULT_ERROR,
      rejectionReason: null,
      analyzedAt: new Date().toISOString(),
    };
  }

  return {
    mode,
    result: data.result,
    rejectionReason: data.rejectionReason ?? null,
    analyzedAt: data.analyzedAt,
  };
}

export async function savePreAnalysisStep(
  stepId: string,
  key: string,
  fileId: string,
  preAnalysis: PreAnalysis,
): Promise<number> {
  const step = await db.vistoriaEtapas
    .where('[idEtapa+chave]')
    .equals([stepId, key])
    .first();

  const photoVideo = step?.fotoVideo;

  if (!photoVideo || photoVideo.nome?.id !== fileId) {
    return photoVideo?.preAnalysisRejections ?? 0;
  }

  photoVideo.preAnalysis = preAnalysis;

  if (
    preAnalysis.mode === PRE_ANALYSIS_MODE_ON &&
    preAnalysis.result === PRE_ANALYSIS_RESULT_REJECTED
  ) {
    photoVideo.preAnalysisRejections =
      (photoVideo.preAnalysisRejections ?? 0) + 1;
  }

  await db.vistoriaEtapas.update([stepId, key], step!);

  return photoVideo.preAnalysisRejections ?? 0;
}

export async function commitStepPhoto(stepId: string, key: string) {
  const step = await db.vistoriaEtapas
    .where('[idEtapa+chave]')
    .equals([stepId, key])
    .first();

  if (step?.fotoVideo) {
    step.fotoVideo.isDraft = false;
    step.sentPhotoBackup = undefined;
    await db.vistoriaEtapas.update([stepId, key], step);
  }
}

export async function isStepPhotoDraft(
  stepId: string,
  key: string,
): Promise<boolean> {
  const step = await db.vistoriaEtapas
    .where('[idEtapa+chave]')
    .equals([stepId, key])
    .first();

  return step?.fotoVideo?.isDraft === true;
}

export async function analyzeStepPhoto(
  key: string,
  stepId: string,
  photoModelId: number,
  fileId: string,
  mode: PreAnalysisMode,
): Promise<{ preAnalysis: PreAnalysis; rejections: number }> {
  const photo = await getPhotoForAnalysis(fileId);

  const preAnalysis = photo
    ? await analyzePhotoAPI(key, photoModelId, photo, mode)
    : withoutPreAnalysis(mode);

  const rejections = await savePreAnalysisStep(
    stepId,
    key,
    fileId,
    preAnalysis,
  );

  return { preAnalysis, rejections };
}
